import type { ColumnConfig, ExtractorConfig, FormatterConfig } from './types'

export type FieldFormatter<T = any> = (value: T, record: any, columnConfig: ColumnConfig) => string

function getFormatterConfig(formatter: ColumnConfig['formatter']): FormatterConfig | null {
  if (!formatter) return null
  if (typeof formatter === 'function') return null
  return formatter
}

function getExtractorConfig(extractor: ColumnConfig['extractor']): ExtractorConfig | null {
  if (!extractor) return null
  return extractor
}

export type FieldExtractor<T = any> = (record: any, columnConfig: ColumnConfig) => T

export const builtInFormatters: Record<string, FieldFormatter> = {
  date: (value: any) => {
    if (!value) return ''

    try {
      const date = new Date(value)
      return date.toISOString().split('T')[0]
    } catch (error) {
      return String(value)
    }
  },

  datetime: (value: any) => {
    if (!value) return ''

    try {
      const date = new Date(value)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch (error) {
      return String(value)
    }
  },

  number: (value: any, record: any, config: ColumnConfig) => {
    if (value === null || value === undefined || value === '') return ''

    const num = Number(value)
    if (isNaN(num)) return String(value)

    const formatter = getFormatterConfig(config.formatter)
    const decimals = formatter?.options?.decimals ?? 2
    return num.toFixed(decimals)
  },

  currency: (value: any, record: any, config: ColumnConfig) => {
    if (value === null || value === undefined || value === '') return ''

    const num = Number(value)
    if (isNaN(num)) return String(value)

    const formatter = getFormatterConfig(config.formatter)
    const currency = formatter?.options?.currency ?? 'USD'
    const locale = formatter?.options?.locale ?? 'en-US'

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(num)
  },

  boolean: (value: any, record: any, config: ColumnConfig) => {
    if (value === null || value === undefined) return ''

    const formatter = getFormatterConfig(config.formatter)
    const trueLabel = formatter?.options?.trueLabel ?? 'Yes'
    const falseLabel = formatter?.options?.falseLabel ?? 'No'

    return value ? trueLabel : falseLabel
  },

  array: (value: any, record: any, config: ColumnConfig) => {
    if (!Array.isArray(value)) return String(value || '')

    const formatter = getFormatterConfig(config.formatter)
    const separator = formatter?.options?.separator ?? ', '
    const maxItems = formatter?.options?.maxItems ?? 10

    const items = value.slice(0, maxItems)
    const result = items.map((item) => String(item)).join(separator)

    if (value.length > maxItems) {
      return `${result} (+${value.length - maxItems} more)`
    }

    return result
  },

  enum: (value: any, record: any, config: ColumnConfig) => {
    if (!value) return ''

    const formatter = getFormatterConfig(config.formatter)
    const enumMap = formatter?.options?.enumMap
    if (enumMap && enumMap[value]) {
      return enumMap[value]
    }

    return String(value)
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  },

  phone: (value: any) => {
    if (!value) return ''

    const cleaned = String(value).replace(/\D/g, '')

    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
    }

    return String(value)
  },

  address: (value: any) => {
    if (!value || typeof value !== 'object') return String(value || '')

    const parts: string[] = []

    if (value.street) parts.push(value.street)
    if (value.city) parts.push(value.city)
    if (value.state) parts.push(value.state)
    if (value.zipCode || value.zip) parts.push(value.zipCode || value.zip)
    if (value.country) parts.push(value.country)

    return parts.join(', ')
  },

  truncate: (value: any, record: any, config: ColumnConfig) => {
    if (!value) return ''

    const formatter = getFormatterConfig(config.formatter)
    const maxLength = formatter?.options?.maxLength ?? 50
    const suffix = formatter?.options?.suffix ?? '...'

    const text = String(value)
    if (text.length <= maxLength) return text

    return text.slice(0, maxLength - suffix.length) + suffix
  },

  fullName: (value: any, record: any, config: ColumnConfig) => {
    if (typeof value === 'string') return value

    const firstNameFields = ['firstName', 'first_name', 'fname']
    const lastNameFields = ['lastName', 'last_name', 'lname']

    let firstName = ''
    let lastName = ''

    for (const field of firstNameFields) {
      if (record[field]) {
        firstName = record[field]
        break
      }
    }

    for (const field of lastNameFields) {
      if (record[field]) {
        lastName = record[field]
        break
      }
    }

    return [firstName, lastName].filter(Boolean).join(' ')
  },

  labelsList: (value: any) => {
    if (!Array.isArray(value)) return ''
    return value
      .map((label: any) => label.labelName || '')
      .filter(Boolean)
      .join(', ')
  },

  websitesList: (value: any) => {
    if (!Array.isArray(value)) return ''
    return value
      .map((website: any) => website.websiteURL || website.url || '')
      .filter(Boolean)
      .join(', ')
  },

  multipleSelect: (value: any) => {
    if (!value) return ''
    try {
      const parsed = Array.isArray(value) ? value : JSON.parse(value as string)
      return Array.isArray(parsed) ? parsed.join(', ') : String(value)
    } catch {
      return String(value)
    }
  },

  filesList: (value: any) => {
    if (!value) return ''
    try {
      const parsed = Array.isArray(value) ? value : JSON.parse(value as string)
      if (Array.isArray(parsed)) {
        return parsed
          .map((file: { fileName?: string; name?: string }) => file.fileName || file.name)
          .filter(Boolean)
          .join(', ')
      }
      return String(value)
    } catch {
      return String(value)
    }
  },

  person: (value: any) => {
    if (!value) return ''
    try {
      const parsed = typeof value === 'object' ? value : JSON.parse(value as string)
      return (parsed as { label?: string })?.label || String(value)
    } catch {
      return String(value)
    }
  },

  text: (value: any) => {
    if (value === null || value === undefined) return ''
    const stringValue = String(value)

    return stringValue.includes('"') ? stringValue.replace(/"/g, '""') : stringValue
  },
}

export const builtInExtractors: Record<string, FieldExtractor> = {
  nested: (record: any, config: ColumnConfig) => {
    const extractor = getExtractorConfig(config.extractor)
    const path = extractor?.options?.path || config.key
    return getNestedValue(record, path)
  },

  firstItem: (record: any, config: ColumnConfig) => {
    const value = getNestedValue(record, config.key)
    return Array.isArray(value) && value.length > 0 ? value[0] : null
  },

  lastItem: (record: any, config: ColumnConfig) => {
    const value = getNestedValue(record, config.key)
    return Array.isArray(value) && value.length > 0 ? value[value.length - 1] : null
  },

  count: (record: any, config: ColumnConfig) => {
    const value = getNestedValue(record, config.key)

    if (Array.isArray(value)) {
      return value.length
    } else if (value && typeof value === 'object') {
      return Object.keys(value).length
    }

    return 0
  },

  mapProperty: (record: any, config: ColumnConfig) => {
    const value = getNestedValue(record, config.key)
    const extractor = getExtractorConfig(config.extractor)
    const property = extractor?.options?.property

    if (!Array.isArray(value) || !property) return null

    return value.map((item) => (item && typeof item === 'object' ? item[property] : item))
  },

  join: (record: any, config: ColumnConfig) => {
    const value = getNestedValue(record, config.key)
    const extractor = getExtractorConfig(config.extractor)
    const separator = extractor?.options?.separator ?? ', '

    if (Array.isArray(value)) {
      return value.join(separator)
    }

    return value
  },
}

export function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined

  try {
    return path.split('.').reduce((current, key) => {
      if (current === null || current === undefined) return undefined

      const arrayMatch = key.match(/^(.+)\[(\d+)\]$/)
      if (arrayMatch) {
        const [, arrayKey, index] = arrayMatch
        const array = current[arrayKey]
        return Array.isArray(array) ? array[parseInt(index, 10)] : undefined
      }

      return current[key]
    }, obj)
  } catch (error) {
    return undefined
  }
}

export function transformRecord(
  record: any,
  columns: ColumnConfig[],
  customFormatters: Record<string, FieldFormatter> = {},
  customExtractors: Record<string, FieldExtractor> = {},
): Record<string, string> {
  const row: Record<string, string> = {}

  for (const column of columns) {
    try {
      const value = extractFieldValue(record, column, customExtractors)

      const formattedValue = formatFieldValue(value, record, column, customFormatters)

      row[column.header] = formattedValue
    } catch (error) {
      row[column.header] = ''
    }
  }

  return row
}

function extractFieldValue(record: any, column: ColumnConfig, customExtractors: Record<string, FieldExtractor>): any {
  if (column.extractor) {
    const extractorName = column.extractor.name
    const extractor = customExtractors[extractorName] || builtInExtractors[extractorName]

    if (extractor) {
      return extractor(record, column)
    }
  }

  return getNestedValue(record, column.key)
}

function formatFieldValue(
  value: any,
  record: any,
  column: ColumnConfig,
  customFormatters: Record<string, FieldFormatter>,
): string {
  if (typeof column.formatter === 'function') {
    return column.formatter(value, record)
  }

  const formatterConfig = getFormatterConfig(column.formatter)
  if (formatterConfig) {
    const formatterName = formatterConfig.name
    const formatter = customFormatters[formatterName] || builtInFormatters[formatterName]

    if (formatter) {
      return formatter(value, record, column)
    }
  }

  if (value === null || value === undefined) {
    return ''
  }

  return String(value)
}

export function transformRecords(
  records: any[],
  columns: ColumnConfig[],
  options: {
    customFormatters?: Record<string, FieldFormatter>
    customExtractors?: Record<string, FieldExtractor>
    onProgress?: (processed: number, total: number) => void
  } = {},
): Record<string, string>[] {
  const { customFormatters = {}, customExtractors = {}, onProgress } = options

  const transformedRecords: Record<string, string>[] = []

  for (let i = 0; i < records.length; i++) {
    try {
      const transformedRecord = transformRecord(records[i], columns, customFormatters, customExtractors)

      transformedRecords.push(transformedRecord)

      if (onProgress && (i + 1) % 100 === 0) {
        onProgress(i + 1, records.length)
      }
    } catch (error) {
      const emptyRow: Record<string, string> = {}
      columns.forEach((col) => {
        emptyRow[col.header] = ''
      })
      transformedRecords.push(emptyRow)
    }
  }

  if (onProgress) {
    onProgress(records.length, records.length)
  }

  return transformedRecords
}

export function validateColumns(columns: ColumnConfig[]): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!Array.isArray(columns) || columns.length === 0) {
    errors.push('Columns array is required and must not be empty')
    return { valid: false, errors }
  }

  const headersSeen = new Set<string>()
  const keysSeen = new Set<string>()

  for (let i = 0; i < columns.length; i++) {
    const column = columns[i]

    if (!column.key) {
      errors.push(`Column at index ${i} is missing required 'key' property`)
    }

    if (!column.header) {
      errors.push(`Column at index ${i} is missing required 'header' property`)
    }

    if (column.header && headersSeen.has(column.header)) {
      errors.push(`Duplicate header found: "${column.header}"`)
    } else if (column.header) {
      headersSeen.add(column.header)
    }

    if (column.key && keysSeen.has(column.key)) {
      errors.push(`Duplicate key found: "${column.key}"`)
    } else if (column.key) {
      keysSeen.add(column.key)
    }

    if (column.formatter && typeof column.formatter !== 'function') {
      const formatterConfig = getFormatterConfig(column.formatter)
      if (formatterConfig && !formatterConfig.name) {
        errors.push(`Column "${column.key}" formatter is missing 'name' property`)
      }
    }

    if (column.extractor) {
      const extractorConfig = getExtractorConfig(column.extractor)
      if (extractorConfig && !extractorConfig.name) {
        errors.push(`Column "${column.key}" extractor is missing 'name' property`)
      }
    }
  }

  return { valid: errors.length === 0, errors }
}

export default {
  transformRecord,
  transformRecords,
  getNestedValue,
  validateColumns,
  builtInFormatters,
  builtInExtractors,
}
